import React, { useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  useDisclosure,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { FiMenu } from 'react-icons/fi';
import SaktiSidebar from '../components/SaktiSidebar';
import SaktiAccessCodeFilterBar from '../components/SaktiAccessCodeFilterBar';
import SaktiAccessCodeTable from '../components/SaktiAccessCodeTable';
import SaktiAccessCodeDetailModal from '../components/SaktiAccessCodeDetailModal';
import { useSaktiAccessCode } from '../hooks/useSaktiAccessCode';
import { SaktiAccessCodeRequest } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

const SaktiAccessCodePage: React.FC = () => {
  const {
    requests,
    total,
    loading,
    draftFilters,
    setFilters,
    applyFilters,
    viewCode,
    cancelRequest,
  } = useSaktiAccessCode();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isMobileSidebarOpen, 
    onOpen: onMobileSidebarOpen, 
    onClose: onMobileSidebarClose 
  } = useDisclosure();

  const [selectedRequest, setSelectedRequest] = useState<SaktiAccessCodeRequest | null>(null);
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleViewCode = async (request: SaktiAccessCodeRequest) => {
    setSelectedRequest(request);
    setActiveCode(null);
    onOpen();
    setLoadingCode(true);
    const code = await viewCode(request.id);
    setActiveCode(code);
    setLoadingCode(false);
  };

  const handleCancel = async (id: string) => {
    if (window.confirm('Apakah Anda yakin ingin membatalkan permintaan kode akses ini?')) {
      setCancelling(true);
      const success = await cancelRequest(id);
      if (success) {
        onClose();
      }
      setCancelling(false);
    }
  };

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        {/* Desktop Sidebar */}
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="access-code" />
        </Box>

        {/* Mobile Sidebar Drawer */}
        <Drawer
          isOpen={isMobileSidebarOpen}
          placement="left"
          onClose={onMobileSidebarClose}
          size="xs"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton zIndex={10} color="gray.500" />
            <DrawerBody p={0}>
              <SaktiSidebar activeMenu="access-code" />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box flex={1} p={SAKTI_UI_SIZE.pagePadding}>
          <VStack align="stretch" spacing={SAKTI_UI_SIZE.sectionGap}>
            <HStack justify="space-between" align={{ base: 'start', md: 'center' }} flexWrap="wrap">
              <HStack spacing={3} align="center">
                <IconButton
                  aria-label="Open menu"
                  icon={<FiMenu />}
                  variant="outline"
                  bg="white"
                  display={{ base: 'flex', xl: 'none' }}
                  onClick={onMobileSidebarOpen}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize={SAKTI_UI_SIZE.pageTitle} fontWeight="bold" color="gray.900" lineHeight="shorter">
                    Manajemen Kode Akses
                  </Text>
                  <Text color="gray.600" fontSize={SAKTI_UI_SIZE.pageSubtitle}>
                    Kelola permintaan aktivasi perangkat mobile
                  </Text>
                </VStack>
              </HStack>
            </HStack>

            <SaktiAccessCodeFilterBar
              filters={draftFilters}
              onFiltersChange={setFilters}
              onApply={applyFilters}
              loading={loading}
            />

            <SaktiAccessCodeTable
              loading={loading}
              requests={requests}
              total={total}
              onViewCode={handleViewCode}
              onCancelRequest={handleCancel}
            />
          </VStack>
        </Box>
      </Flex>

      <SaktiAccessCodeDetailModal
        isOpen={isOpen}
        onClose={onClose}
        request={selectedRequest}
        code={activeCode}
        loadingCode={loadingCode}
        onCancel={handleCancel}
        cancelling={cancelling}
      />
    </Box>
  );
};

export default SaktiAccessCodePage;
